import React, { useState, useRef, useEffect } from 'react';
import { IconX, IconCamera, IconCheck, IconAlertCircle, IconEye, IconEyeOff } from '@tabler/icons-react';
import api from '../api/api';

export default function ProfileModal({ me, onClose, onUpdated }) {
  const [nickname, setNickname]           = useState(me?.nickname || '');
  const [password, setPassword]           = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [showPw, setShowPw]               = useState(false);
  const [showPwConfirm, setShowPwConfirm] = useState(false);
  const [preview, setPreview]             = useState(me?.profileImage || null);
  const [imageBase64, setImageBase64]     = useState(undefined); // undefined = 변경 안 함
  const [saving, setSaving]               = useState(false);
  const [error, setError]                 = useState('');
  const [success, setSuccess]             = useState(false);
  const fileRef = useRef(null);

  // 배경 스크롤 방지
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      setError('이미지는 2MB 이하만 업로드 가능합니다.');
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      setPreview(ev.target.result);
      setImageBase64(ev.target.result);
    };
    reader.readAsDataURL(file);
  };

  const removeImage = () => {
    setPreview(null);
    setImageBase64(''); // 빈 문자열 = 이미지 삭제
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleSave = async () => {
    setError('');
    if (password && password !== passwordConfirm) {
      setError('비밀번호가 일치하지 않습니다.');
      return;
    }
    if (password && password.length < 6) {
      setError('비밀번호는 6자 이상이어야 합니다.');
      return;
    }

    setSaving(true);
    try {
      const body = { nickname };
      if (password) body.password = password;
      if (imageBase64 !== undefined) body.profileImage = imageBase64;

      const res = await api.patch('/auth/me', body);
      setSuccess(true);
      onUpdated(res.data);
      setTimeout(() => {
        setSuccess(false);
        onClose();
      }, 1000);
    } catch {
      setError('저장에 실패했습니다. 다시 시도해 주세요.');
    } finally {
      setSaving(false);
    }
  };

  const initials = (me?.nickname || me?.name || me?.email || 'A')[0].toUpperCase();

  return (
    <>
      {/* 오버레이 */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0,
          background: 'rgba(0,0,0,0.45)',
          zIndex: 3000,
        }}
      />

      {/* 모달 */}
      <div style={{
        position: 'fixed',
        top: '50%', left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '100%', maxWidth: 480,
        background: 'var(--tblr-bg-surface, #fff)',
        borderRadius: 12,
        boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
        zIndex: 3001,
        overflow: 'hidden',
      }}>

        {/* 헤더 */}
        <div style={{
          padding: '20px 24px',
          borderBottom: '1px solid var(--tblr-border-color, #e6e7e9)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>내 정보 수정</h3>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', cursor: 'pointer',
            padding: 4, borderRadius: 6, display: 'flex', alignItems: 'center',
            color: 'var(--tblr-muted, #9aa0ac)',
          }}>
            <IconX size={20} />
          </button>
        </div>

        {/* 바디 */}
        <div style={{ padding: '24px', overflowY: 'auto', maxHeight: 'calc(100vh - 200px)' }}>

          {/* 에러/성공 메시지 */}
          {error && (
            <div className="alert alert-danger mb-4" style={{ borderRadius: 8, border: 'none' }}>
              <div className="d-flex align-items-center gap-2">
                <IconAlertCircle size={16} style={{ flexShrink: 0 }} />
                <span style={{ fontSize: 14 }}>{error}</span>
              </div>
            </div>
          )}
          {success && (
            <div className="alert alert-success mb-4" style={{ borderRadius: 8, border: 'none' }}>
              <div className="d-flex align-items-center gap-2">
                <IconCheck size={16} style={{ flexShrink: 0 }} />
                <span style={{ fontSize: 14 }}>저장되었습니다!</span>
              </div>
            </div>
          )}

          {/* 프로필 이미지 */}
          <div className="mb-4 d-flex flex-column align-items-center">
            <div style={{ position: 'relative', width: 96, height: 96 }}>
              {preview ? (
                <img
                  src={preview}
                  alt="profile"
                  style={{
                    width: 96, height: 96,
                    borderRadius: '50%',
                    objectFit: 'cover',
                    border: '3px solid var(--tblr-primary, #206bc4)',
                  }}
                />
              ) : (
                <div style={{
                  width: 96, height: 96,
                  borderRadius: '50%',
                  background: 'var(--tblr-primary, #206bc4)',
                  color: '#fff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 36, fontWeight: 700,
                  border: '3px solid var(--tblr-primary, #206bc4)',
                }}>
                  {initials}
                </div>
              )}
              {/* 카메라 아이콘 버튼 */}
              <button
                onClick={() => fileRef.current?.click()}
                style={{
                  position: 'absolute', bottom: 0, right: 0,
                  width: 30, height: 30,
                  borderRadius: '50%',
                  background: 'var(--tblr-primary, #206bc4)',
                  border: '2px solid var(--tblr-bg-surface, #fff)',
                  cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#fff',
                }}
              >
                <IconCamera size={14} />
              </button>
            </div>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleImageChange}
            />
            <div className="d-flex gap-2 mt-2">
              <button
                className="btn btn-sm btn-outline-secondary"
                onClick={() => fileRef.current?.click()}
                style={{ fontSize: 12 }}
              >
                이미지 변경
              </button>
              {preview && (
                <button
                  className="btn btn-sm btn-outline-danger"
                  onClick={removeImage}
                  style={{ fontSize: 12 }}
                >
                  삭제
                </button>
              )}
            </div>
            <div className="text-muted mt-1" style={{ fontSize: 11 }}>
              JPG, PNG, GIF · 최대 2MB
            </div>
          </div>

          {/* 아이디 (읽기 전용) */}
          <div className="mb-3">
            <label className="form-label fw-medium" style={{ fontSize: 14 }}>아이디</label>
            <input
              type="text"
              className="form-control form-control-sm"
              value={me?.email || ''}
              disabled
              style={{ background: 'var(--tblr-bg-surface-secondary, #f4f6fa)', fontSize: 14 }}
            />
          </div>

          {/* 이름 (읽기 전용) */}
          <div className="mb-3">
            <label className="form-label fw-medium" style={{ fontSize: 14 }}>이름</label>
            <input
              type="text"
              className="form-control form-control-sm"
              value={me?.name || ''}
              disabled
              style={{ background: 'var(--tblr-bg-surface-secondary, #f4f6fa)', fontSize: 14 }}
            />
          </div>

          {/* 닉네임 */}
          <div className="mb-3">
            <label className="form-label fw-medium" style={{ fontSize: 14 }}>
              닉네임
              <span className="text-muted fw-normal ms-1" style={{ fontSize: 12 }}>(없으면 이름으로 표시)</span>
            </label>
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="닉네임을 입력하세요"
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
              style={{ fontSize: 14 }}
            />
          </div>

          {/* 새 비밀번호 */}
          <div className="mb-3">
            <label className="form-label fw-medium" style={{ fontSize: 14 }}>
              새 비밀번호
              <span className="text-muted fw-normal ms-1" style={{ fontSize: 12 }}>
                (변경 시에만 입력)
              </span>
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPw ? 'text' : 'password'}
                className="form-control form-control-sm"
                placeholder="새 비밀번호 (6자 이상)"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                style={{ paddingRight: 40, fontSize: 14 }}
              />
              <button
                type="button"
                onClick={() => setShowPw(v => !v)}
                style={{
                  position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
                  background: 'none', border: 'none', cursor: 'pointer', padding: 4,
                  color: 'var(--tblr-muted, #9aa0ac)',
                }}
              >
                {showPw ? <IconEyeOff size={16} /> : <IconEye size={16} />}
              </button>
            </div>
          </div>

          {/* 비밀번호 확인 */}
          <div className="mb-1">
            <label className="form-label fw-medium" style={{ fontSize: 14 }}>비밀번호 확인</label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPwConfirm ? 'text' : 'password'}
                className="form-control form-control-sm"
                placeholder="비밀번호를 다시 입력하세요"
                value={passwordConfirm}
                onChange={(e) => setPasswordConfirm(e.target.value)}
                style={{
                  paddingRight: 40, fontSize: 14,
                  borderColor: passwordConfirm && password !== passwordConfirm
                    ? 'var(--tblr-danger, #d63939)'
                    : undefined,
                }}
              />
              <button
                type="button"
                onClick={() => setShowPwConfirm(v => !v)}
                style={{
                  position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
                  background: 'none', border: 'none', cursor: 'pointer', padding: 4,
                  color: 'var(--tblr-muted, #9aa0ac)',
                }}
              >
                {showPwConfirm ? <IconEyeOff size={16} /> : <IconEye size={16} />}
              </button>
            </div>
            {passwordConfirm && password !== passwordConfirm && (
              <div className="text-danger mt-1" style={{ fontSize: 12 }}>
                비밀번호가 일치하지 않습니다.
              </div>
            )}
          </div>

        </div>

        {/* 푸터 */}
        <div style={{
          padding: '16px 24px',
          borderTop: '1px solid var(--tblr-border-color, #e6e7e9)',
          display: 'flex', gap: 8, justifyContent: 'flex-end',
        }}>
          <button className="btn btn-sm btn-outline-secondary" onClick={onClose} disabled={saving}>
            취소
          </button>
          <button
            className="btn btn-sm btn-primary"
            onClick={handleSave}
            disabled={saving}
            style={{ minWidth: 80 }}
          >
            {saving ? (
              <><span className="spinner-border spinner-border-sm me-2" style={{ width: 14, height: 14 }} />저장 중</>
            ) : (
              <><IconCheck size={16} className="me-1" />저장</>
            )}
          </button>
        </div>

      </div>
    </>
  );
}
